/**
 * FRONTIER F9-4 — Adaptive role-personalized UI: the entitlement-bounded ranker.
 *
 * ─── The honesty crux ───────────────────────────────────────────────────────
 * Every function here is a PURE REORDER over an already-permitted input. None of
 * them takes usage data and produces an item; they take the permitted set the
 * shell already resolved (role-gated by `menuRegistry.getVisibleMenus`, then
 * entitlement-rule-filtered by `resolveVisibleModules`) and return a permutation
 * of exactly that set. Because the input is the authoritative permitted list and
 * the output is a permutation of it (same membership, only order changes), it is
 * structurally impossible for adaptation to surface an item the user isn't
 * entitled to — a usage count for a non-permitted id is simply never present in
 * the input and is dropped.
 *
 * This is asserted by construction (`stableRankByUsage` only sorts; it never
 * inserts) and guarded at runtime by `assertSameMembership` in dev.
 */

import type { MenuItem, SidebarSection } from '@tyndo/ui-kit';
import type { UsageCounts } from './usageStore';

/**
 * Dev-only invariant: the ranked output must contain exactly the same ids as the
 * permitted input — no additions (which would be an entitlement escalation) and
 * no drops (which would strand an entitled surface). Throws in dev so a
 * regression is caught in tests; no-ops in production so it can never break the
 * shell for an operator.
 */
export function assertSameMembership(input: readonly string[], output: readonly string[]): void {
  const isProd = (() => {
    try {
      return typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';
    } catch {
      return false;
    }
  })();
  if (isProd) return;
  const a = [...input].sort();
  const b = [...output].sort();
  const same = a.length === b.length && a.every((v, i) => v === b.at(i));
  if (!same) {
    throw new Error(
      `[adaptiveNav] ranking changed membership — this would breach the entitlement boundary. in=[${a.join(',')}] out=[${b.join(',')}]`
    );
  }
}

/**
 * Stable, usage-weighted sort of already-permitted items.
 *
 * `keyOf` maps an item to the usage-signal key (its module id). Items with a
 * higher recorded count sort first; ties and unused items keep their original
 * relative order (a stable sort by original index), so with NO usage data the
 * result is byte-identical to the input — the honest cold-start default.
 */
export function stableRankByUsage<T>(
  items: readonly T[],
  keyOf: (item: T) => string | undefined,
  counts: UsageCounts
): T[] {
  const decorated = items.map((item, index) => {
    const k = keyOf(item);
    const weight = k ? counts[k] ?? 0 : 0;
    return { item, index, weight };
  });
  decorated.sort((a, b) => {
    if (b.weight !== a.weight) return b.weight - a.weight;
    return a.index - b.index; // stable: preserve manifest/registry order on ties
  });
  return decorated.map((d) => d.item);
}

/**
 * Map a rendered sidebar `MenuItem` back to the module-id usage key. The shell
 * builds items with `id = menuId`; the owning module id is the leading
 * `tyndo.<mod>` token of the menuId (menu ids are namespaced by module). We
 * fall back to the raw id so an unnamespaced item still ranks deterministically.
 */
export function moduleKeyForMenuItem(item: MenuItem): string {
  const id = item.id ?? '';
  // menuIds look like `tyndo.production.workorders`; the module id is the first
  // two dotted tokens (`tyndo.production`). Anything shorter uses the whole id.
  const parts = id.split('.');
  if (parts.length >= 2 && parts.at(0) === 'tyndo') {
    return `${parts.at(0)}.${parts.at(1)}`;
  }
  return id;
}

/**
 * Re-rank the items *within* each already-filtered sidebar section by usage.
 * Sections themselves keep their ISA-95 / module order; only the leaf items
 * inside a section are surfaced-by-frequency. Deprioritized ≠ removed: every
 * permitted item is still present and reachable — nothing is hidden, only
 * reordered. Returns the input sections unchanged when adaptation is disabled or
 * there is no signal.
 */
export function adaptSidebarSections(
  sections: readonly SidebarSection[],
  counts: UsageCounts,
  enabled: boolean
): SidebarSection[] {
  if (!enabled) return sections as SidebarSection[];
  return sections.map((section) => {
    const items = section.items ?? [];
    const ranked = stableRankByUsage(items, moduleKeyForMenuItem, counts);
    // Runtime guard: the ranked items must be the SAME permitted set, reordered.
    assertSameMembership(
      items.map((i) => i.id ?? ''),
      ranked.map((i) => i.id ?? '')
    );
    return { ...section, items: ranked };
  });
}

/** A surface the dashboard/landing can default to, tagged with its usage key. */
export interface RankableSurface {
  /** Usage-signal key (module id). */
  key: string;
}

/**
 * Choose the persona's most-used surfaces for the adaptive dashboard, bounded to
 * the `permitted` set. The candidate list is the ALREADY-PERMITTED surfaces; we
 * rank them by usage and take the top `limit`. When there is no signal (or
 * adaptation is off) we return the permitted candidates in their original order,
 * i.e. the honest default — never a fabricated pick.
 */
export function adaptDashboardSurfaces<T extends RankableSurface>(
  permitted: readonly T[],
  counts: UsageCounts,
  enabled: boolean,
  limit: number
): T[] {
  const ordered = enabled ? stableRankByUsage(permitted, (s) => s.key, counts) : [...permitted];
  assertSameMembership(
    permitted.map((s) => s.key),
    ordered.map((s) => s.key)
  );
  return ordered.slice(0, Math.max(0, limit));
}

/**
 * The copilot-scope config the copilot rail can read: an ordered list of the
 * persona's emphasized module ids (most-used first), plus whether adaptation is
 * active. Bounded to the permitted set — the copilot rail must intersect its own
 * suggestions with the platform's entitlement checks regardless; this only
 * changes EMPHASIS ORDER, never grants a scope.
 */
export interface CopilotScopeConfig {
  /** Whether the ordering below is usage-adapted (vs. default). */
  adapted: boolean;
  /** Permitted module ids, most-emphasized first. */
  emphasizedModuleIds: string[];
}

export function buildCopilotScope(
  permittedModuleIds: readonly string[],
  counts: UsageCounts,
  enabled: boolean
): CopilotScopeConfig {
  const ranked = enabled
    ? stableRankByUsage(permittedModuleIds, (id) => id, counts)
    : [...permittedModuleIds];
  assertSameMembership(permittedModuleIds, ranked);
  return { adapted: enabled, emphasizedModuleIds: ranked };
}

/**
 * Total recorded interactions across all surfaces — used to decide whether the
 * UI has enough signal to *show* the "adapted" state honestly (0 ⇒ cold-start).
 */
export function totalUsage(counts: UsageCounts): number {
  let sum = 0;
  for (const v of Object.values(counts)) sum += v;
  return sum;
}
